import { useEffect, useRef, useState } from "react";
import { getSupabase } from "../lib/supabase";
import { ensureProfile } from "../lib/profile";
import { useLang } from "../lib/prefs";

const T = {
  es: { working: "Iniciando sesión…", failed: "No se pudo completar el inicio de sesión.", back: "Volver a entrar" },
  en: { working: "Signing you in…", failed: "Could not complete sign-in.", back: "Back to sign in" },
};

// /auth/callback — Google redirects here with a PKCE `?code=`. We exchange it
// for a session, ensure the user's profiles row exists, then enter the app.
export default function Callback() {
  const [lang] = useLang();
  const [error, setError] = useState<string | null>(null);
  const ran = useRef(false);
  const t = T[lang];

  useEffect(() => {
    if (ran.current) return; // guard React StrictMode double-invoke in dev
    ran.current = true;

    (async () => {
      const supabase = getSupabase();
      const params = new URLSearchParams(window.location.search);
      const oauthError = params.get("error_description") ?? params.get("error");
      if (oauthError) {
        setError(oauthError);
        return;
      }

      // Session may already exist (e.g. re-entering the route); else exchange.
      let session = (await supabase.auth.getSession()).data.session;
      const code = params.get("code");
      if (!session && code) {
        const { data, error } = await supabase.auth.exchangeCodeForSession(code);
        if (error) {
          // A concurrent exchange may have consumed the code — re-check.
          session = (await supabase.auth.getSession()).data.session;
          if (!session) {
            setError(error.message);
            return;
          }
        } else {
          session = data.session;
        }
      }

      if (!session) {
        window.location.replace("/login");
        return;
      }

      await ensureProfile(supabase, session.user);

      // If we were mid-way through connecting an MCP client (the /connect flow
      // stashed a return URL before the Google round-trip), go back there so the
      // user can finish authorizing; otherwise enter the app.
      const connectReturn = sessionStorage.getItem("citada_connect_return");
      if (connectReturn) {
        sessionStorage.removeItem("citada_connect_return");
        window.location.replace(connectReturn);
        return;
      }
      window.location.replace("/");
    })();
  }, []);

  if (error) {
    return (
      <div className="auth">
        <div className="auth-card">
          <div className="auth-logo">citada</div>
          <p className="auth-err" style={{ marginTop: 20 }}>
            {t.failed}
          </p>
          <a className="g-btn" href="/login">
            {t.back}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="auth">
      <div className="auth-card">
        <div className="auth-logo">citada</div>
        <p className="lead" style={{ margin: "18px 0 0" }}>
          {t.working}
        </p>
        <div className="spinner" />
      </div>
    </div>
  );
}
